import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { useRouter } from '#app/hooks/useRouter.js';
import * as actionTypes from '../actionTypes.js';
import { ToastContext } from '../contexts/ToastContext.js';
import { useContext, useEffect, useMemo } from 'react';

/**
 * Do NOT export this for general consumption. This is a private hook.
 */
export const useToaster = () => {
  const { playwrightTest } = useAppConfiguration();

  const {
    dispatch,
    state: { pausedAt, toasts },
    toast,
  } = useContext(ToastContext);
  const visibleToasts = toasts.filter((tst) => tst.visible);
  const { subscribe } = useRouter();

  useEffect(() => {
    const dispose = subscribe({
      onTransitionStart: () => {
        if (playwrightTest) {
          return;
        }

        const toastsToDismiss = toasts.filter(
          (tst) => tst.dismissOnRouteChange,
        );
        for (const tst of toastsToDismiss) {
          if (tst.visible) {
            toast.dismiss(tst.id);
          }
        }
      },
    });

    return dispose;
  }, [playwrightTest, subscribe, toast, toasts]);

  useEffect(() => {
    if (playwrightTest) {
      return () => {};
    }

    if (pausedAt) {
      return () => {};
    }

    const now = Date.now();

    const timeouts = toasts.map((tst) => {
      if (tst.autoDismiss === false) return undefined;

      const durationLeft =
        (tst.duration ?? 0) + tst.pauseDuration - (now - tst.createdAt);

      if (durationLeft < 0) {
        if (tst.visible) {
          toast.dismiss(tst.id);
        }

        return undefined;
      }

      return setTimeout(() => {
        toast.dismiss(tst.id);
      }, durationLeft);
    });

    return () => {
      for (const timeout of timeouts) {
        if (timeout) {
          clearTimeout(timeout);
        }
      }
    };
  }, [pausedAt, playwrightTest, toast, toasts]);

  const handlers = useMemo(
    () => ({
      calculateOffset: (toastId: string, options?: { margin?: number }) => {
        const { margin = 8 } = options ?? {};

        const index = visibleToasts.findIndex((tst) => tst.id === toastId);

        const offset =
          index >= 0
            ? visibleToasts
                .slice(0, index)
                .reduce(
                  (accumulator, tst) =>
                    accumulator + (tst.height ?? 0) + margin,
                  0,
                )
            : 0;

        return offset;
      },
      endPause: () => {
        if (pausedAt) {
          dispatch({
            payload: { time: Date.now() },
            type: actionTypes.END_PAUSE,
          });
        }
      },
      startPause: () => {
        dispatch({
          payload: {
            time: Date.now(),
          },
          type: actionTypes.START_PAUSE,
        });
      },
      updateHeight: (toastId: string, height: number) => {
        dispatch({
          payload: { toast: { height, id: toastId } },
          type: actionTypes.UPDATE_TOAST,
        });
      },
    }),
    [dispatch, pausedAt, visibleToasts],
  );

  return {
    handlers,
    toasts,
    visibleToasts,
  };
};
